import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";

import { useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionForm, useCurrentComposition } from "@design-lab/entities/composition";
import { useVisitedTabs } from "@design-lab/shared/hooks/useVisitedTabs";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { ValidationContext } from "@design-lab/shared/hooks/useValidation";

export function ValidationProvider(props: PropsWithChildren) {
  const { children } = props;

  const { form: artworkForm } = useArtworkForm();
  const artworkFormValues = useWatch({ control: artworkForm.control });
  const { form: compositionForm } = useCompositionForm();
  const compositionFormValues = useWatch({ control: compositionForm.control });
  const { front, back, someArtworkLoading } = useArtwork();
  const { currentComposition, currentProduct } = useCurrentComposition();

  const [visitedTabs] = useVisitedTabs();
  const experiments = useExperiments();

  const showSubTabs = useMemo(
    () => shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs),
    [experiments?.abDesignLabReorderTabs]
  );

  const isFrontArtworkProcessing = Boolean(front.state.hasTag("loading"));
  const isFrontArtworkUploaded = Boolean(
    artworkFormValues?.front?.artworkId || artworkFormValues?.frontLeftChest?.artworkId
  );

  const isBackArtworkProcessing = Boolean(back.state.hasTag("loading"));
  const isBackArtworkUploaded = Boolean(artworkFormValues?.back?.artworkId);

  const isArtworkUploaded = isFrontArtworkUploaded || isBackArtworkUploaded;
  const hasProduct = Boolean(currentProduct);

  const addedCompositions = compositionFormValues?.addedCompositions ?? [];
  const currentAddedComposition = addedCompositions.find(
    composition => composition.id === currentComposition?.id
  );

  const isQuantityValid = Object.values(currentAddedComposition?.variants || {}).some(
    quantity => Number(quantity) > 0
  );

  const isAllQuantitiesValid =
    compositionFormValues?.addedCompositions?.some(composition =>
      Object.values(composition.variants || {}).some(quantity => Number(quantity) > 0)
    ) || false;

  const isAtLeastOneArtworkProcessed =
    isArtworkUploaded && (!isFrontArtworkProcessing || !isBackArtworkProcessing);

  const isFileSelected = Boolean(
    front.state.context.visitedStates.includes("progress") ||
    front.state.matches("finished") ||
    back.state.context.visitedStates.includes("progress") ||
    back.state.matches("finished")
  );

  const isFrontArtworkEnhanced = front.state.matches("finished");
  const isBackArtworkEnhanced = back.state.matches("finished");
  const isArtworkEnhanced = isFrontArtworkEnhanced || isBackArtworkEnhanced;

  const value = useMemo(
    () => ({
      isFileSelected,
      isArtworkUploaded,
      isArtworkEnhanced,
      isQuantityValid,
      isAllQuantitiesValid,
      hasProduct,
      isAtLeastOneArtworkProcessed,
      isSomeArtworkLoading: someArtworkLoading,
      isFrontArtworkUploaded,
      isBackArtworkUploaded,
      visitedTabs,
      isFrontArtworkEnhanced,
      isBackArtworkEnhanced,
      showSubTabs
    }),
    [
      isFileSelected,
      isArtworkUploaded,
      isArtworkEnhanced,
      isQuantityValid,
      isAllQuantitiesValid,
      hasProduct,
      isAtLeastOneArtworkProcessed,
      someArtworkLoading,
      isFrontArtworkUploaded,
      isBackArtworkUploaded,
      visitedTabs,
      isFrontArtworkEnhanced,
      isBackArtworkEnhanced,
      showSubTabs
    ]
  );

  return <ValidationContext.Provider value={value}>{children}</ValidationContext.Provider>;
}
